import React, { useContext, useCallback, useRef } from "react";
import { GetUsers_users as umsUser } from "src/api/generated/GetUsers";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { Route } from "src/config/routes";
import { observer } from "mobx-react-lite";
import { UI, ScreenSize, DropdownItem } from "@wwimmo/ui";

interface UserListProps {
    users: umsUser[];
    totalEntries: number;
    currentPage: number;
    itemsPerPage: number;
    onPageChanged: (currentPage: number) => void;
    sortItems: DropdownItem[];
    onSearch: (searchArguments: { searchQuery?: string; filterItemValue?: string; sortItemValue?: string }) => void;
    onChangeSort: (searchArguments: { searchQuery?: string; filterItemValue?: string; sortItemValue?: string }) => void;
    searchAppendElement?: React.ReactNode;
}

const UserList = (props: UserListProps) => {
    const { t } = useTranslation();

    const { uiStore, navStore } = useContext(RootStoreContext);
    const { onPageChanged: onPageChangedProp } = props;

    const listIdentifier = "userlist";

    if (!navStore.listMap.has(listIdentifier)) {
        navStore.setListMapValue(listIdentifier, {});
    }

    const listParameters = navStore.getListMapValue(listIdentifier);

    const initialSearchQuery = useRef<string | undefined>(listParameters?.searchQuery ?? undefined).current;
    const initialSortQuery = useRef<string | undefined>(listParameters?.sortValue ?? undefined).current;

    const onRowClick = useCallback(() => {
        navStore.setListMapValue(
            listIdentifier,
            listParameters
                ? { ...listParameters, scrollPosition: window.pageYOffset }
                : { scrollPosition: window.pageYOffset }
        );

        navStore.setBackbutton(Route.users, t("common.users"));
    }, [navStore, t, listParameters]);

    const onPageChanged = useCallback(
        (currentPage: number) => {
            navStore.setListMapValue(
                listIdentifier,
                listParameters
                    ? { ...listParameters, scrollPosition: window.pageYOffset, paginationPage: currentPage }
                    : { scrollPosition: window.pageYOffset, paginationPage: currentPage }
            );

            onPageChangedProp(currentPage);
        },
        [navStore, listParameters, onPageChangedProp]
    );

    const renderItem = useCallback(
        (user: umsUser, screenSize: ScreenSize) => {
            let listRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={user.name2 ? user.name2 : ""} />
                            <UI.List.Cell key={"c-2"} colspan={1} value={user.name1 ? user.name1 : ""} />
                            <UI.List.Cell key={"c-3"} colspan={1} value={user.email ? user.email : ""} />
                            <UI.List.Cell key={"c-4"} colspan={1} value={user.customername ? user.customername : ""} />
                            <UI.List.Cell key={"c-5"} colspan={0.3} value={user.registered ? "Ja" : "Nein"} />
                            <UI.List.Cell key={"c-6"} colspan={0.3} value={user.activated ? "Ja" : "Nein"} />
                            <UI.List.Cell key={"c-7"} colspan={0.3} value={user.system ? "Ja" : "Nein"} />
                            <UI.List.Cell key={"c-8"} colspan={0.3} value={user.blocked ? "Ja" : "Nein"} />
                            <UI.List.Cell key={"c-9"} colspan={0.3} value={user.erpcloud ? "Ja" : "Nein"} />
                        </UI.List.Row>
                    ];
                    break;
                case ScreenSize.MOBILE:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={user.name2 ? user.name2 : ""} />
                        </UI.List.Row>
                    ];
                    break;
            }

            const link = `/users/${user.customerid}/${user.userid}`;

            return (
                <UI.List.Item
                    key={`${user.userid}-${user.customerid}`}
                    screenSize={screenSize}
                    rows={listRows}
                    to={link}
                    onClick={onRowClick}
                />
            );
        },
        [onRowClick]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            let headerRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    headerRows = [
                        <UI.List.Row key={"row-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={t("user.last_name")} />
                            <UI.List.Cell key={"c-2"} colspan={1} value={t("user.first_name")} />
                            <UI.List.Cell key={"c-3"} colspan={1} value={t("user.email")} />
                            <UI.List.Cell key={"c-4"} colspan={1} value={t("user.customer")} />
                            <UI.List.Cell key={"c-5"} colspan={0.3} value={t("user.registered")} />
                            <UI.List.Cell key={"c-6"} colspan={0.3} value={t("user.activated")} />
                            <UI.List.Cell key={"c-7"} colspan={0.3} value={t("user.system")} />
                            <UI.List.Cell key={"c-8"} colspan={0.3} value={t("user.locked")} />
                            <UI.List.Cell key={"c-9"} colspan={0.3} value={"ERP Cloud"} />
                        </UI.List.Row>
                    ];
                    break;
                case ScreenSize.MOBILE:
                    headerRows = [
                        <UI.List.Row key={"row-1"}>
                            <UI.List.Cell key={"c-1"} colspan={1} value={t("user.last_name")} />
                        </UI.List.Row>
                    ];
                    break;
            }

            return <UI.List.Header rows={headerRows} sticky={true} top={130} />;
        },
        [t]
    );

    const initialSortItem = props.sortItems.find((item) => item.value === initialSortQuery);

    return (
        <>
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={props.users}
                renderItem={renderItem}
                renderHeader={renderHeader}
                scrollPosition={listParameters?.scrollPosition ?? undefined}
                itemsPerPage={Math.max(props.users.length, 1)}
                initialPage={1}
                onSearch={props.onSearch}
                initialSearchQuery={initialSearchQuery}
                sortItems={props.sortItems}
                initialSortItem={initialSortItem}
                onChangeSort={props.onChangeSort}
            />
            {props.totalEntries > props.itemsPerPage ? (
                <div className="mt-3 d-flex justify-content-center">
                    <UI.Pagination
                        onPageChanged={onPageChanged}
                        numberOfDataEntries={props.totalEntries}
                        dataEntriesPerPage={props.itemsPerPage}
                        initialPage={props.currentPage}
                    />
                </div>
            ) : null}
        </>
    );
};

export default observer(UserList);
